/// Day, month-word, year — in that order, always. A locale default puts the
/// month first for en-US readers, which is the bug this module exists to
/// prevent; only the month word itself localizes. Times are 24h, computed from
/// the parts rather than `hour12: false` (which yields "24:00" at midnight in
/// some locales).

type DateInput = string | number | Date;

interface Shape {
  /// "Aug" instead of "August" — for table cells and chips.
  short?: boolean;
  year?: boolean;
  time?: boolean;
}

function dayMonthYear(
  value: DateInput,
  { short = false, year = true, time = false }: Shape,
): string {
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "";

  const month = d.toLocaleDateString(undefined, {
    month: short ? "short" : "long",
  });
  let out = `${d.getDate()} ${month}`;
  if (year) out += ` ${d.getFullYear()}`;
  if (time) {
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    out += `, ${hh}:${mm}`;
  }
  return out;
}

/// "28 August 2026"
export const formatDayMonthYear = (value: DateInput): string =>
  dayMonthYear(value, {});

/// "28 August 2026, 14:32"
export const formatDayMonthYearTime = (value: DateInput): string =>
  dayMonthYear(value, { time: true });

/// "1 October" — a date near enough that the year is implied. Used for an
/// allowance reset: it is always the next month boundary, at most 31 days out,
/// and the year would read as noise beside "Since 8 September".
export const formatDayMonth = (value: DateInput): string =>
  dayMonthYear(value, { year: false });

/// The local `Date` whose wall clock reads `value` in UTC, for a picker that
/// only knows local time; `fromUtcWall` is its inverse. A UTC time inside the
/// local clock's spring-forward hour cannot be shown and shifts by that hour.
export const toUtcWall = (value: DateInput): Date => {
  const d = value instanceof Date ? value : new Date(value);
  return new Date(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate(),
    d.getUTCHours(),
    d.getUTCMinutes(),
  );
};

export const fromUtcWall = (wall: Date): Date =>
  new Date(
    Date.UTC(
      wall.getFullYear(),
      wall.getMonth(),
      wall.getDate(),
      wall.getHours(),
      wall.getMinutes(),
    ),
  );

/// "28 August 2026, 14:32 UTC"
export const formatDayMonthYearTimeUtc = (value: DateInput): string =>
  `${formatDayMonthYearTime(toUtcWall(value))} UTC`;

/// "28 Aug 2026"
export const formatShortDate = (value: DateInput): string =>
  dayMonthYear(value, { short: true });

/// "28 Aug 2026, 14:32"
export const formatShortDateTime = (value: DateInput): string =>
  dayMonthYear(value, { short: true, time: true });

/// "28 Aug" — a date in the coming weeks, beside "28 Aug, 14:32".
export const formatShortDayMonth = (value: DateInput): string =>
  dayMonthYear(value, { short: true, year: false });

/// "28 Aug, 14:32" — for lists where the year is implied by context.
export const formatDayMonthTime = (value: DateInput): string =>
  dayMonthYear(value, { short: true, year: false, time: true });

/// "14:32" — 24h, for a row whose day is established by its group heading.
export const formatTime = (value: DateInput): string => {
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

/// The heading a day's worth of events sits under: "Today", "Yesterday", or
/// the full date. Relative only for the two days a reader holds in their head.
export const formatDayLabel = (value: DateInput): string => {
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "";
  const midnight = (x: Date) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((midnight(new Date()) - midnight(d)) / 86_400_000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return formatDayMonthYear(d);
};

/// Groups events into days, newest first, keeping each day's own order.
export const groupByDay = <T>(
  items: T[],
  at: (item: T) => string,
): { key: string; label: string; items: T[] }[] => {
  const out: { key: string; label: string; items: T[] }[] = [];
  for (const item of items) {
    const stamp = at(item);
    const key = formatDayMonthYear(stamp);
    const last = out[out.length - 1];
    if (last?.key === key) last.items.push(item);
    else out.push({ key, label: formatDayLabel(stamp), items: [item] });
  }
  return out;
};

/// The stored value's unit isn't guaranteed (seconds vs ms), so normalise
/// before diffing — a 1000× slip would show a wildly wrong countdown.
export const deletionCountdown = (
  raw: bigint | number,
): { label: string; tooltip: string } => {
  const value = Number(raw);
  const ms = value < 1e12 ? value * 1000 : value;
  const days = Math.ceil((ms - Date.now()) / 86_400_000);
  if (days <= 0) {
    return { label: "<1d", tooltip: "Permanently deleted very soon" };
  }
  return {
    label: `${days}d`,
    tooltip: `Permanently deleted in ${days} day${days === 1 ? "" : "s"}`,
  };
};
